/**
 * Tiny fetch wrapper that:
 * - Always sends cookies (admin session is an httpOnly cookie)
 * - Throws on non-2xx with the JSON `error` field if available
 * - Returns parsed JSON
 */
export async function api<T = unknown>(
  path: string,
  options: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = options;
  const init: RequestInit = {
    credentials: 'include',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  };
  const resp = await fetch(path, init);
  let body: unknown = undefined;
  const text = await resp.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!resp.ok) {
    const msg =
      (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string')
        ? body.error
        : `HTTP ${resp.status}`;
    const err = new Error(msg) as Error & { status?: number; body?: unknown };
    err.status = resp.status;
    err.body = body;
    throw err;
  }
  return body as T;
}
